import { FC, ReactNode, useCallback, useMemo } from 'react';
import { FormProvider, FormState, useForm } from 'react-hook-form';
import { Grid } from '@mui/material';

import { BoxWell } from '@shared/components/BoxWell';
import { StructuralAnalysis } from '@modules/Analysis/partials/StructuralAnalysis';
import { Blocker } from '@cvt/components/Blocker';

import { StoryField } from './StoryField';
import { OutlineFields } from './OutlineFields';
import { MetadataFields } from './MetadataFields';
import { CharacterDevelopmentFields } from './CharacterDevelopmentFields';


export type FormCrud = Stories.Create | Stories.Edit;

interface Props {
    defaultValues?: Partial<FormCrud>;
    onSubmitRequest: (data: FormCrud) => void;
    slots?: {
      footer?: ReactNode | ((formState: FormState<FormCrud>) => ReactNode);
    },
}

const DEFAULT_VALUES: Partial<FormCrud> = {
  title: '',
  type: '',
  content: '',
  status: 'Idea',
  objectives: [],
  values: [],
  tags: [],
  emotionalArc: undefined,
  characters: {
    protagonist: {
      name: '',
      want: '',
      need: '',
      change: '',
    },
    antagonist: {
      name: '',
      description: '',
    },
    supportcharacters: [],
  },
};

export const StoryForm: FC<Props> = (props) => {
  const { defaultValues, onSubmitRequest, slots } = props;
  const storyId = useMemo(() => {
    if (!defaultValues) return undefined;
    return 'id' in defaultValues ? (defaultValues as Stories.Edit).id : undefined;
  }, [defaultValues]);



  const mergedDefaultValues = useMemo(() => {
    const initialValues = {
      ...DEFAULT_VALUES,
      ...defaultValues,
    };
    return {
      ...initialValues,
      objectives: initialValues.objectives || [],
      values: initialValues.values || [],
      tags: initialValues.tags || [],
      setting: initialValues.setting || [],
      coreMessage: initialValues.coreMessage || '',
      emotionalArc: initialValues.emotionalArc || null,
      title: initialValues.title || '',
      content: initialValues.content || '',
      theme: initialValues.theme || '',
      notes: initialValues.notes || '',
      structuralFeedback: initialValues.structuralFeedback || undefined,
      characters: {
        protagonist: {
          name: initialValues.characters?.protagonist?.name || '',
          want: initialValues.characters?.protagonist?.want || '',
          need: initialValues.characters?.protagonist?.need || '',
          change: initialValues.characters?.protagonist?.change || '',
        },
        antagonist: {
          name: initialValues.characters?.antagonist?.name || '',
          description: initialValues.characters?.antagonist?.description || '',
        },
        supportcharacters: initialValues.characters?.supportcharacters || [],
      },
    };
  }, [defaultValues]);

  const methods = useForm<FormCrud>({
    defaultValues: mergedDefaultValues,
  });

  const { handleSubmit, formState, reset } = methods;

  const onSubmit = useCallback(async (data: FormCrud) => {
    await onSubmitRequest(data);
    reset();
  }, [onSubmitRequest, reset]);

  return (
    <FormProvider {...methods}>
      <form id="story-form" onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <BoxWell sx={{ height: '100%', p: { xs: 2, sm: 5 } }}>
              <MetadataFields defaultValues={mergedDefaultValues}/>
            </BoxWell>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <BoxWell sx={{ height: '100%', p: { xs: 2, sm: 5 } }}>
              <OutlineFields/>
            </BoxWell>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <BoxWell sx={{ p: { xs: 2, sm: 5 } }}>
              <CharacterDevelopmentFields/>
            </BoxWell>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <BoxWell sx={{ p: { xs: 2, sm: 5 } }}>
              <StoryField/>
            </BoxWell>
          </Grid>

          {storyId && (
            <Grid size={{ xs: 12 }}>
              <BoxWell sx={{ p: { xs: 2, sm: 5 } }}>
                <StructuralAnalysis storyId={storyId} loadingText="No analysis yet"/>
              </BoxWell>
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            {slots?.footer ? (
              typeof slots.footer === 'function' ? slots.footer(formState) : slots.footer
            ) : null}
          </Grid>
        </Grid>
      </form>
      <Blocker shouldBlock={formState.isDirty}/>
    </FormProvider>
  );
};
